import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  LinearProgress,
  Link,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ArrowForwardRounded as OpenIcon,
  AutoAwesome as NewPagesIcon,
  Contrast as OledIcon,
  DarkModeOutlined as DarkIcon,
  ExpandMoreRounded as ExpandIcon,
  FolderOutlined as ProjectsIcon,
  GroupsOutlined as PeopleIcon,
  HelpOutlineRounded as HelpIcon,
  LightModeOutlined as LightIcon,
  SettingsBrightnessOutlined as SystemIcon,
  TodayOutlined as MonthIcon,
  TuneRounded as PreferencesIcon,
} from '@mui/icons-material';
import PageHeader, { PageContainer } from '../components/PageHeader';
import { UserContext } from '../components/MiniDrawer';
import { PersonAvatar, RoleChip } from '../components/projects/PeoplePanels';
import StatusChip from '../components/projects/StatusChip';
import { cardSx, GREEN, GREEN_DARK, GREEN_SOFT, INK, LINE, MUTED } from '../components/home/homeTheme';
import { formatHours, MONTH_NAMES } from '../components/monthly/format';
import { segmentedSx } from '../components/monthly/MultiSelectFilter';
import { getCurrentEnvironment, SUPPORT_CONFIG } from '../config/local.config';
import { useHomeMonth } from '../hooks/useHomeMonth';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { getRoleDisplayName } from '../utils/roleManager';
import { fetchProfile } from '../utils/profileService';

const PEOPLE_MANAGER_ORG_ROLE = 2;

function Card({ icon: Icon, title, action, children }) {
  return (
    <Box sx={{ ...cardSx, p: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Icon sx={{ fontSize: 20, color: MUTED }} />
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK, flex: 1 }}>{title}</Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

function Person({ user, caption }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      <PersonAvatar name={user.username} size={36} />
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>
          {user.username}
        </Typography>
        <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
          {caption ?? (
            <Link href={`mailto:${user.email}`} underline="hover" sx={{ color: 'inherit' }}>
              {user.email}
            </Link>
          )}
        </Typography>
      </Box>
    </Box>
  );
}

const linkButtonSx = { textTransform: 'none', fontWeight: 600, color: GREEN_DARK, px: 1, minWidth: 0 };

/**
 * Profile V2: chi è l'utente in RUNTime (ruoli, People Manager, riporti), su cosa lavora (progetti),
 * come va il mese, le preferenze dell'interfaccia e i riferimenti per l'aiuto.
 */
export default function ProfileV2() {
  const navigate = useNavigate();
  const { backendUser, cognitoUser, preferences } = useContext(UserContext);
  const { hasPageAccess } = useRoleAccess();
  const userId = backendUser?.id;

  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    fetchProfile(userId)
      .then((p) => !cancelled && setProfile(p))
      .catch((e) => !cancelled && setError(`Unable to load your profile: ${e.message}`));
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const today = new Date();
  const month = useHomeMonth(userId, today.getFullYear(), today.getMonth() + 1);

  const me = { ...backendUser, ...(profile?.me || {}) };
  const roleName = getRoleDisplayName(Number(me.role_id));
  const orgRoleName = me.org_role?.name || me.org_role_name;
  const roleDescription = me.role?.description;
  const isPeopleManager = Number(me.org_role_id) === PEOPLE_MANAGER_ORG_ROLE;
  const email = me.email || cognitoUser?.email;

  const expected = month.expectedToDate || 0;
  const tracked = month.trackedToDate || 0;
  const progress = expected ? Math.min(100, (tracked / expected) * 100) : 0;
  const toComplete = month.incompleteDays?.length || 0;

  if (!backendUser) {
    return (
      <PageContainer>
        <PageHeader title="Profile" />
        <Alert severity="warning">Your account isn’t in the RUNTime database yet, so there is no profile to show. Contact support to be added.</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader title="Profile" />

      {/* Chi sei */}
      <Box sx={{ ...cardSx, p: 2.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <PersonAvatar name={me.username} size={64} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: INK }}>{me.username || email}</Typography>
          <Typography sx={{ fontSize: 13.5, color: MUTED }}>{email}</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
            <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 999, fontSize: 12, fontWeight: 700, bgcolor: GREEN_SOFT, color: GREEN_DARK }}>
              {roleName}
            </Box>
            {orgRoleName && (
              <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 999, fontSize: 12, fontWeight: 700, bgcolor: 'var(--rt-track)', color: INK }}>
                {orgRoleName}
              </Box>
            )}
          </Box>
          {roleDescription && <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.75 }}>{roleDescription}</Typography>}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.6fr) minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {/* Questo mese */}
          <Card
            icon={MonthIcon}
            title={`This month · ${MONTH_NAMES[today.getMonth()]}`}
            action={
              <Button size="small" endIcon={<OpenIcon />} onClick={() => navigate('/monthly-user-view')} sx={linkButtonSx}>
                My month
              </Button>
            }
          >
            {!month.ready ? (
              <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={22} color="success" />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: INK, fontVariantNumeric: 'tabular-nums' }}>{formatHours(tracked)}h</Typography>
                  <Typography sx={{ fontSize: 13.5, color: MUTED }}>of {formatHours(expected)}h expected so far · {formatHours(month.monthTarget || 0)}h in the month</Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3, bgcolor: 'var(--rt-track)', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: toComplete ? '#E9A23B' : GREEN } }} />
                <Typography sx={{ fontSize: 12.5, color: toComplete ? 'var(--rt-amber)' : GREEN_DARK, fontWeight: 700, mt: 1 }}>
                  {toComplete ? `${toComplete} day${toComplete === 1 ? '' : 's'} to complete` : 'All caught up'}
                </Typography>
              </>
            )}
          </Card>

          {/* Progetti */}
          <Card icon={ProjectsIcon} title={`Your projects${profile ? ` · ${profile.projects.length}` : ''}`}>
            {!profile ? (
              <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'center' }}>{!error && <CircularProgress size={22} color="success" />}</Box>
            ) : !profile.projects.length ? (
              <Typography sx={{ fontSize: 13.5, color: MUTED }}>You aren’t assigned to any project yet.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', mx: -2.25, borderTop: `1px solid ${LINE}` }}>
                {profile.projects.map((p) => {
                  const canOpen = p.id != null && hasPageAccess(`/projects/${p.id}`);
                  return (
                    <ButtonBase
                      key={p.id}
                      disabled={!canOpen}
                      onClick={() => navigate(`/projects/${p.id}`)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'flex-start', textAlign: 'left', px: 2.25, py: 1, borderBottom: `1px solid ${LINE}`, '&:hover': { bgcolor: canOpen ? 'var(--rt-hover)' : 'transparent' } }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: p.active ? INK : MUTED }}>
                          {p.name}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
                          {[p.customer, p.code, p.teams.length && `Team: ${p.teams.join(', ')}`].filter(Boolean).join(' · ') || '—'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {p.roles.map((r) => (
                          <RoleChip key={r.id} code={r.code} name={r.name} />
                        ))}
                        {!p.active && <StatusChip active={false} />}
                      </Box>
                    </ButtonBase>
                  );
                })}
              </Box>
            )}
          </Card>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {/* Persone */}
          <Card icon={PeopleIcon} title="People">
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>Your People Manager</Typography>
            {!profile ? (
              <Typography sx={{ fontSize: 13.5, color: MUTED }}>…</Typography>
            ) : profile.manager ? (
              <Person user={profile.manager} />
            ) : (
              <Typography sx={{ fontSize: 13.5, color: MUTED }}>No People Manager assigned.</Typography>
            )}
            {profile && profile.reports.length > 0 && (
              <Box sx={{ mt: 2, pt: 1.75, borderTop: `1px solid ${LINE}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', flex: 1 }}>
                    Your direct reports · {profile.reports.length}
                  </Typography>
                  {isPeopleManager && (
                    <Button size="small" endIcon={<OpenIcon />} onClick={() => navigate('/monthly-people-mng-view')} sx={linkButtonSx}>
                      People overview
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {profile.reports.slice(0, 5).map((r) => (
                    <Person key={r.id} user={r} />
                  ))}
                  {profile.reports.length > 5 && <Typography sx={{ fontSize: 12.5, color: MUTED }}>and {profile.reports.length - 5} more</Typography>}
                </Box>
              </Box>
            )}
          </Card>

          {/* Preferenze */}
          {preferences && (
            <Card icon={PreferencesIcon} title="Preferences">
              {preferences.v2Available && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75 }}>
                  <NewPagesIcon sx={{ fontSize: 20, color: preferences.v2Pages ? GREEN : MUTED }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>New pages</Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>Use the redesigned pages. Turning this off brings back the classic ones.</Typography>
                  </Box>
                  <Switch color="success" checked={!!preferences.v2Pages} onChange={preferences.toggleV2Pages} inputProps={{ 'aria-label': 'Use the new pages' }} />
                </Box>
              )}
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>Theme</Typography>
              <Typography sx={{ fontSize: 12, color: MUTED, mb: 1 }}>Applies to the new pages; classic pages stay light.</Typography>
              <ToggleButtonGroup exclusive fullWidth size="small" value={preferences.themePref} onChange={(e, v) => v && preferences.setThemePref(v)} sx={{ ...segmentedSx, '& .MuiToggleButton-root': { ...segmentedSx['& .MuiToggleButton-root'], gap: 0.5, px: 1 } }}>
                <ToggleButton value="light">
                  <LightIcon sx={{ fontSize: 16 }} /> Light
                </ToggleButton>
                <ToggleButton value="dark">
                  <DarkIcon sx={{ fontSize: 16 }} /> Dark
                </ToggleButton>
                <ToggleButton value="oled">
                  <OledIcon sx={{ fontSize: 16 }} /> OLED
                </ToggleButton>
                <ToggleButton value="system">
                  <SystemIcon sx={{ fontSize: 16 }} /> Auto
                </ToggleButton>
              </ToggleButtonGroup>
            </Card>
          )}

          {/* Aiuto */}
          <Card icon={HelpIcon} title="Help">
            <Typography sx={{ fontSize: 13.5, color: INK }}>
              Something wrong or missing? Write to{' '}
              <Link href={`mailto:${SUPPORT_CONFIG.email}`} underline="hover" sx={{ color: GREEN_DARK, fontWeight: 600 }}>
                {SUPPORT_CONFIG.email}
              </Link>
              .
            </Typography>
            <Accordion disableGutters elevation={0} sx={{ mt: 1.5, bgcolor: 'transparent', border: `1px solid ${LINE}`, borderRadius: '10px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: MUTED }}>Technical details</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography sx={{ fontSize: 12, color: MUTED, mb: 1 }}>Useful to include when you open a ticket.</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5, fontSize: 12.5 }}>
                  {[
                    ['User ID', me.id],
                    ['Email', email],
                    ['App role', `${roleName} (${me.role_id})`],
                    ['Environment', getCurrentEnvironment()],
                  ].map(([k, v]) => (
                    <React.Fragment key={k}>
                      <Typography sx={{ fontSize: 12.5, color: MUTED }}>{k}</Typography>
                      <Typography sx={{ fontSize: 12.5, color: INK, fontFamily: 'monospace', wordBreak: 'break-all' }}>{v ?? '—'}</Typography>
                    </React.Fragment>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Card>
        </Box>
      </Box>
    </PageContainer>
  );
}
